import {
  Dropdown,
  DropdownRef,
  Input,
  Loader,
  Menu,
  Tabs,
  Tooltip,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import {
  AlertDiamondFilled,
  ChevronDown,
  ChevronUp,
  Database_01,
  Indeed,
  Plus,
  Star_06,
} from '@recruit-robin/robin-theme/web-icons';
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { ConnectToAtsProps, ConnectToVacancyDropdownRef } from './ConnectToVacancyDropdown.types';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { CreateJob } from '../VacancyList/components/CreateJob/CreateJob';
import { VacancyListTab } from '../VacancyList/VacancyList.types';
import { useStyles } from './ConnectToVacancyDropdown.styles';
import { AtsTab } from './screens/AtsTab';
import { IndeedTab } from './screens/IndeedTab';
import { useVacancyActions } from './useVacancyActions';

const SyncOpen = ({ isOpen, onChange }: { isOpen: boolean; onChange: (v: boolean) => void }) => {
  useEffect(() => {
    onChange(isOpen);
  }, [isOpen, onChange]);
  return null;
};

export const ConnectToVacancyDropdown = forwardRef<ConnectToVacancyDropdownRef, ConnectToAtsProps>(
  (
    {
      onSelectVacancy,
      selectedVacancy,
      contained = false,
      onlyAtsVacancies = true,
      variant = 'default',
      initialTab = VacancyListTab.AtsJobs,
      enabledTabs = [VacancyListTab.AtsJobs],
      error,
      isProcessingVacancy,
      onWatchIsOpen,
      hideMessageInfo = false,
      atsOnly = false,
    },
    ref,
  ) => {
    const { dictionary } = useContext(LocalizationContext);
    const classes = useStyles({ contained });

    const [isSelectionLoading, setIsSelectionLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState(initialTab);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<DropdownRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          dropdownRef.current?.open();
          inputRef.current?.focus();
        },
      }),
      [],
    );

    const atsProps = useMemo(() => {
      if (!selectedVacancy) return { name: null, icon: Plus };

      return {
        name: selectedVacancy.name,
        icon: selectedVacancy?.is_improved ? Star_06 : selectedVacancy.type === 'INDEED' ? Indeed : Database_01,
      };
    }, [selectedVacancy]);

    const handleSelect = async (vacancy: Vacancy | null) => {
      try {
        vacancy !== null && setIsSelectionLoading(true);

        await onSelectVacancy(vacancy);
      } finally {
        vacancy !== null && setIsSelectionLoading(false);

        dropdownRef.current?.close();
      }
    };

    const { handleCreateJob, handleDeleteVacancy, handleEditVacancy } = useVacancyActions(selectedTab, onSelectVacancy);

    return (
      <div css={classes.root} ref={containerRef}>
        <Dropdown
          css={[classes.dropdown, isProcessingVacancy && classes.dropdownProcessingVacancy]}
          ref={dropdownRef}
          renderMenu={
            <Menu.Root css={classes.menu}>
              <Tabs.Root customCss={classes.tabsRoot} value={selectedTab} onValueChange={setSelectedTab}>
                <Tabs.List customCss={classes.tabsList}>
                  <Tabs.Indicator />
                  {enabledTabs.includes(VacancyListTab.IndeedJobs) && (
                    <Tabs.Trigger customCss={classes.tabs} value={VacancyListTab.IndeedJobs}>
                      <Indeed size={16} />
                      <Typography variant={(v) => v.supporting.label}>{dictionary.indeedJobs}</Typography>
                    </Tabs.Trigger>
                  )}

                  {enabledTabs.includes(VacancyListTab.AtsJobs) && (
                    <Tabs.Trigger customCss={classes.tabs} value={VacancyListTab.AtsJobs}>
                      <Database_01 size={16} />
                      <Typography variant={(v) => v.supporting.label}>{dictionary.atsJobs}</Typography>
                    </Tabs.Trigger>
                  )}

                  <CreateJob
                    onDisconnectVacancy={() => handleSelect(null)}
                    selectedVacancy={selectedVacancy}
                    onCreateJob={handleCreateJob}
                  />
                </Tabs.List>
                {enabledTabs.includes(VacancyListTab.IndeedJobs) && (
                  <IndeedTab
                    key={VacancyListTab.IndeedJobs}
                    onCreateJob={handleCreateJob}
                    onSelectVacancy={handleSelect}
                    onEditVacancy={handleEditVacancy}
                    onDeleteVacancy={handleDeleteVacancy}
                    selectedVacancy={selectedVacancy}
                    search={search}
                    hideMessageInfo={hideMessageInfo}
                  />
                )}
                {enabledTabs.includes(VacancyListTab.AtsJobs) && (
                  <AtsTab
                    key={VacancyListTab.AtsJobs}
                    onCreateJob={handleCreateJob}
                    onSelectVacancy={handleSelect}
                    onEditVacancy={handleEditVacancy}
                    onDeleteVacancy={handleDeleteVacancy}
                    selectedVacancy={selectedVacancy}
                    search={search}
                    hideMessageInfo={hideMessageInfo}
                    atsOnly={atsOnly}
                  />
                )}
              </Tabs.Root>
            </Menu.Root>
          }>
          {({ isOpen }) => (
            <>
              <SyncOpen isOpen={isOpen} onChange={(v) => onWatchIsOpen?.(v)} />
              <Input
                ref={inputRef}
                variant={variant}
                customCss={[!isOpen && classes.inputClosed, error && classes.inputError]}
                startIcon={
                  isSelectionLoading || isProcessingVacancy ? <Loader size={14} /> : atsProps.icon({ size: 14 })
                }
                endIcon={
                  error ? (
                    <Tooltip label={error} placement="top" zIndex={3} offset={{ mainAxis: 8 }}>
                      <AlertDiamondFilled color={(c) => c.tertiary.red[50]} size={14} />
                    </Tooltip>
                  ) : isOpen ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )
                }
                onInput={(e) => setSearch(e.currentTarget.value)}
                placeholder={dictionary.searchVacancy}
                value={
                  isOpen ? search : atsProps.name ?? (onlyAtsVacancies ? dictionary.connectToAts : dictionary.addJob)
                }
                readOnly={isProcessingVacancy}
                contained
              />
            </>
          )}
        </Dropdown>
      </div>
    );
  },
);
